import React, { useEffect } from 'react';
import { TravelAbilityWordmark } from './TravelAbilityLogo';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { assessmentData } from '../data/assessmentData';
import { getActionPlan } from '../data/actionPlanData';
import { 
  Target, TrendingUp, Trophy, HelpCircle,
  CheckCircle2, Clock, Star, Zap, Rocket, Shield, DollarSign, Play
} from 'lucide-react';

const profileIcons = {
  'awakening': Target,
  'momentum': TrendingUp,
  'advanced': Trophy,
  'uncertain': HelpCircle
};

export const AssessmentResults = ({ lead, results, onRestart }) => {
  const { profile, answers } = results;
  const ProfileIcon = profileIcons[profile.id] || Target;

  // Get budget tier from Q2 compound answer
  const budgetTierId = answers?.[2]?.compound?.budget?.id || null;
  const bandwidthAnswer = answers?.[2]?.compound?.bandwidth?.text || '';
  const budgetAnswer = answers?.[2]?.compound?.budget?.text || '';
  const actionPlan = getActionPlan(budgetTierId);

  useEffect(() => { window.scrollTo(0, 0); }, []);

  const tierColors = ['#3B82F6', '#10B981', '#8B5CF6', '#F59E0B', '#EF4444'];
  const tierIcons = [DollarSign, Zap, Rocket, Shield, Trophy];

  return (
    <div className="min-h-screen bg-[#111111]">
      <div className="fixed inset-0 bg-cover bg-center opacity-10"
        style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1749976609807-dc05ec2dbbdb?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjd8MHwxfHNlYXJjaHwyfHxpbmNsdXNpdmUlMjB0b3VyaXNtJTIwZGl2ZXJzZSUyMHBlb3BsZSUyMHRyYXZlbGluZ3xlbnwwfHx8fDE3NzU5MjE2NzB8MA&ixlib=rb-4.1.0&q=85)' }} />
      <div className="fixed inset-0 bg-gradient-to-b from-[#111111]/80 via-[#111111]/90 to-[#111111]" />

      <div className="relative max-w-4xl mx-auto px-4 sm:px-6 py-12">
        <div className="flex justify-center mb-8"><TravelAbilityWordmark /></div>

        {/* Header */}
        <div className="text-center mb-10">
          <h1 className="text-3xl sm:text-4xl font-bold text-[#F3F4F6] mb-2" data-testid="results-title">
            YOUR ACCESSIBILITY BUILD-A-PLAN
          </h1>
          <p className="text-lg text-[#9CA3AF]">
            Personalized for {lead.name} at {lead.organization}
          </p>
        </div>

        {/* Profile Card */}
        <Card className="mb-8 overflow-hidden" style={{ borderColor: profile.color, borderWidth: '2px' }}>
          <div className="h-2" style={{ backgroundColor: profile.color }} />
          <CardContent className="p-8">
            <div className="flex items-start gap-5 mb-6">
              <div className="p-4 rounded-2xl" style={{ backgroundColor: `${profile.color}20` }}>
                <ProfileIcon className="w-10 h-10" style={{ color: profile.color }} />
              </div>
              <div>
                <p className="text-sm text-[#9CA3AF] mb-1">Your Profile</p>
                <h2 className="text-2xl sm:text-3xl font-bold" style={{ color: profile.color }} data-testid="profile-title">
                  {profile.title}
                </h2>
                <p className="text-sm text-[#6B7280] mt-1 italic">{profile.pattern}</p>
              </div>
            </div>
            <p className="text-[#9CA3AF] text-lg leading-relaxed mb-6">{profile.summary}</p>

            {/* Bandwidth + Budget Summary */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
              {bandwidthAnswer && (
                <div className="p-4 bg-[#222222] rounded-xl">
                  <p className="text-sm text-[#9CA3AF]">Your Bandwidth</p>
                  <p className="text-[#F3F4F6] font-bold">{bandwidthAnswer}/month</p>
                </div>
              )}
              {budgetAnswer && (
                <div className="p-4 bg-[#222222] rounded-xl">
                  <p className="text-sm text-[#9CA3AF]">Your Budget</p>
                  <p className="text-[#FBBF24] font-bold">{budgetAnswer}</p>
                </div>
              )}
            </div>

            {/* Current State */}
            <div className="p-5 bg-[#222222] rounded-xl mb-6">
              <h4 className="font-bold text-[#F3F4F6] mb-3">Your Current State:</h4>
              <ul className="space-y-2">
                {profile.currentState.map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <span className="w-1.5 h-1.5 rounded-full mt-2.5 flex-shrink-0" style={{ backgroundColor: profile.color }} />
                    <span className="text-[#9CA3AF]">{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* What This Means */}
            <div className="p-5 bg-[#222222] rounded-xl">
              <h4 className="font-bold text-[#FBBF24] mb-2">What This Means:</h4>
              <p className="text-[#9CA3AF] leading-relaxed">{profile.whyFits}</p>
            </div>
          </CardContent>
        </Card>

        {/* ═══════════ ACTION PLAN ═══════════ */}
        <div className="mb-8">
          <div className="text-center mb-6">
            <h2 className="text-2xl font-bold text-[#F3F4F6] mb-2">
              Your Action Plan
            </h2>
            <p className="text-[#9CA3AF]">
              Based on your bandwidth and budget — tiers are cumulative
            </p>
          </div>

          {/* CORE ACTIONS (Original Rubric) */}
          <Card className="mb-4 bg-[#1A1A1A] border-2 border-[#FBBF24]">
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-3 text-[#FBBF24]">
                <Star className="w-6 h-6" />
                {actionPlan.coreActions.label}
              </CardTitle>
              <p className="text-sm text-[#9CA3AF]">{actionPlan.coreActions.description}</p>
            </CardHeader>
            <CardContent>
              <ul className="space-y-2">
                {actionPlan.coreActions.actions.map((action, i) => (
                  <li key={i} className="flex items-start gap-3 p-3 bg-[#222222] rounded-lg">
                    <CheckCircle2 className="w-5 h-5 text-[#FBBF24] mt-0.5 flex-shrink-0" />
                    <span className="text-[#F3F4F6]">{action}</span>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>

          {/* ADDITIONAL ACTIONS */}
          <Card className="mb-4 bg-[#1A1A1A] border-[#333333] border-l-4 border-l-[#10B981]">
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-3 text-[#10B981]">
                <Zap className="w-5 h-5" />
                {actionPlan.additionalActions.label}
              </CardTitle>
              <p className="text-sm text-[#9CA3AF]">{actionPlan.additionalActions.description}</p>
            </CardHeader>
            <CardContent>
              <ul className="space-y-2">
                {actionPlan.additionalActions.actions.map((action, i) => (
                  <li key={i} className="flex items-start gap-3 p-3 bg-[#222222] rounded-lg">
                    <CheckCircle2 className="w-5 h-5 text-[#10B981] mt-0.5 flex-shrink-0" />
                    <span className="text-[#F3F4F6]">{action}</span>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>

          {/* WE DON'T BITE VIDEO CTA */}
          <a 
            href="https://www.youtube.com/watch?v=Xkz-UNuvve0&t=1s"
            target="_blank"
            rel="noopener noreferrer"
            className="block mb-6"
            data-testid="we-dont-bite-btn"
          >
            <div className="relative overflow-hidden rounded-xl border-2 border-[#EF4444] bg-gradient-to-r from-[#EF4444]/20 via-[#EF4444]/10 to-[#EF4444]/20 p-6 hover:from-[#EF4444]/30 hover:via-[#EF4444]/15 hover:to-[#EF4444]/30 transition-all group cursor-pointer">
              <div className="flex items-center justify-center gap-4">
                <div className="w-14 h-14 rounded-full bg-[#EF4444] flex items-center justify-center group-hover:scale-110 transition-transform">
                  <Play className="w-7 h-7 text-white ml-1" />
                </div>
                <div>
                  <p className="text-xl font-bold text-[#F3F4F6]">Watch: "We Don't Bite"</p>
                  <p className="text-sm text-[#9CA3AF]">ODR Disability Sensitivity Training Video</p>
                </div>
              </div>
            </div>
          </a>

          {/* BUDGET-TIERED ACTIONS */}
          {actionPlan.budgetGroups.length > 0 && (
            <div className="mb-4">
              <h3 className="text-lg font-bold text-[#F3F4F6] mb-4 flex items-center gap-2">
                <DollarSign className="w-5 h-5 text-[#FBBF24]" />
                Budget-Specific Actions
              </h3>

              {actionPlan.budgetGroups.map((budgetGroup, bgIdx) => {
                const color = tierColors[bgIdx] || '#3B82F6';
                const TierIcon = tierIcons[bgIdx] || DollarSign;

                return (
                  <div key={bgIdx} className="mb-4">
                    {/* Tier Header */}
                    <div className="flex items-center gap-3 mb-3 p-3 rounded-lg" style={{ backgroundColor: `${color}15`, borderLeft: `4px solid ${color}` }}>
                      <TierIcon className="w-5 h-5" style={{ color }} />
                      <div>
                        <span className="font-bold text-[#F3F4F6]">{budgetGroup.tierLabel}</span>
                        {budgetGroup.note && (
                          <span className="text-sm text-[#6B7280] ml-2">({budgetGroup.note})</span>
                        )}
                      </div>
                    </div>

                    {/* Groups within this tier */}
                    {budgetGroup.groups.map((group, gIdx) => (
                      <Card key={gIdx} className="mb-3 bg-[#1A1A1A] border-[#333333]">
                        <CardHeader className="pb-2 pt-4">
                          <CardTitle className="text-sm font-semibold uppercase tracking-wider" style={{ color }}>
                            {group.category}
                          </CardTitle>
                        </CardHeader>
                        <CardContent className="pt-0">
                          <ul className="space-y-2">
                            {group.actions.map((action, aIdx) => (
                              <li key={aIdx} className="flex items-start gap-3 p-3 bg-[#222222] rounded-lg">
                                <CheckCircle2 className="w-5 h-5 mt-0.5 flex-shrink-0" style={{ color }} />
                                <span className="text-[#9CA3AF]">{action}</span>
                              </li>
                            ))}
                          </ul>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Timeline */}
        <div className="flex items-center gap-3 p-4 bg-[#1A1A1A] border border-[#333333] rounded-xl mb-8">
          <Clock className="w-5 h-5 text-[#FBBF24]" />
          <span className="text-[#F3F4F6] font-semibold">Timeline:</span>
          <span className="text-[#9CA3AF]">{profile.timeline}</span>
        </div>

        {/* What Happens Next */}
        <Card className="mb-8 bg-[#1A1A1A] border-[#333333]">
          <CardHeader>
            <CardTitle className="text-[#F3F4F6] text-center">What Happens Next</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {assessmentData.whatHappensNext.map((item) => (
                <div key={item.step} className="flex items-start gap-4 p-4 bg-[#222222] rounded-xl">
                  <span className="w-8 h-8 flex items-center justify-center rounded-full bg-[#FBBF24]/20 text-[#FBBF24] font-bold text-sm">{item.step}</span>
                  <div>
                    <p className="text-[#F3F4F6]">{item.text}</p>
                    <p className="text-sm text-[#6B7280]">{item.time}</p>
                  </div>
                </div>
              ))}
            </div>
            <p className="text-center text-[#FBBF24] font-semibold mt-6">No pressure. Just clarity.</p>
          </CardContent>
        </Card>

        {/* Credibility */}
        <Card className="mb-8 bg-[#1A1A1A] border-[#333333]">
          <CardHeader>
            <CardTitle className="text-[#F3F4F6] text-center">Why TravelAbility?</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {assessmentData.credibility.stats.map((stat, i) => (
                <div key={i} className="flex items-start gap-3 p-3 bg-[#222222] rounded-xl">
                  <CheckCircle2 className="w-5 h-5 text-[#10B981] mt-0.5 flex-shrink-0" />
                  <span className="text-[#9CA3AF]">{stat}</span>
                </div>
              ))}
            </div>
            <p className="text-center text-xl font-bold text-[#FBBF24] mt-6">{assessmentData.credibility.closing}</p>
          </CardContent>
        </Card>

        {/* Bottom */}
        <div className="text-center">
          <Button onClick={onRestart} variant="outline"
            className="border-[#333333] text-[#9CA3AF] hover:text-[#F3F4F6] hover:border-[#FBBF24]"
            data-testid="restart-btn">
            Take Assessment Again
          </Button>
        </div>

        {/* Footer */}
        <footer className="mt-12 pt-8 border-t border-[#333333] text-center">
          <p className="text-[#6B7280] text-sm">TravelAbility - Your Accessibility Partner</p>
          <p className="text-[#6B7280] text-sm mt-2">
            Questions? Email{' '}
            <a href={`mailto:${assessmentData.contact.email}`} className="text-[#FBBF24] hover:underline">{assessmentData.contact.email}</a>
          </p>
        </footer>
      </div>
    </div>
  );
};

export default AssessmentResults;
